import { validateFloorplanDocument, type FloorplanDocumentV2 } from "./floorplan-document.ts";

const DATABASE_NAME = "planform-v2";
const STORE_NAME = "projects";
const SUMMARY_STORE_NAME = "project-summaries";

export type LocalProjectSummary = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  sourceName: string;
  floorCount: number;
  furnishingCount: number;
};

export function summarizeProject(document: FloorplanDocumentV2): LocalProjectSummary {
  return {
    id: document.id,
    name: document.name,
    createdAt: document.createdAt,
    updatedAt: document.updatedAt,
    sourceName: document.source.name,
    floorCount: document.levels.length,
    furnishingCount: document.furnishings?.length ?? 0,
  };
}

export function sortProjectSummaries(summaries: LocalProjectSummary[]) {
  return [...summaries].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 2);
    request.onupgradeneeded = () => {
      const database = request.result;
      const transaction = request.transaction!;
      const projects = database.objectStoreNames.contains(STORE_NAME)
        ? transaction.objectStore(STORE_NAME)
        : database.createObjectStore(STORE_NAME, { keyPath: "id" });
      if (database.objectStoreNames.contains(SUMMARY_STORE_NAME)) return;
      const summaries = database.createObjectStore(SUMMARY_STORE_NAME, { keyPath: "id" });
      // Version 1 stored only full documents. Build a lightweight index during
      // the upgrade so the launch library does not have to load floorplan images.
      const cursor = projects.openCursor();
      cursor.onsuccess = () => {
        const entry = cursor.result;
        if (!entry) return;
        try { summaries.put(summarizeProject(validateFloorplanDocument(entry.value))); } catch { /* keep malformed legacy records hidden */ }
        entry.continue();
      };
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveProjectLocally(document: FloorplanDocumentV2) {
  if (typeof indexedDB === "undefined") throw new Error("Local storage is not available.");
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME, SUMMARY_STORE_NAME], "readwrite");
    transaction.objectStore(STORE_NAME).put(document);
    transaction.objectStore(SUMMARY_STORE_NAME).put(summarizeProject(document));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error("Save was interrupted."));
  });
  database.close();
}

export async function loadLatestProjectLocally() {
  for (const summary of await listProjectSummaries()) {
    const project = await loadProjectLocally(summary.id);
    if (project) return project;
  }
  return null;
}

export async function listProjectSummaries() {
  if (typeof indexedDB === "undefined") return [] as LocalProjectSummary[];
  const database = await openDatabase();
  try {
    const summaries = await new Promise<LocalProjectSummary[]>((resolve, reject) => {
      const request = database.transaction(SUMMARY_STORE_NAME, "readonly").objectStore(SUMMARY_STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result as LocalProjectSummary[]);
      request.onerror = () => reject(request.error);
    });
    return sortProjectSummaries(summaries);
  } finally { database.close(); }
}

export function serializeProject(document: FloorplanDocumentV2) {
  return JSON.stringify(document, null, 2);
}

export async function loadProjectLocally(id: string): Promise<FloorplanDocumentV2 | null> {
  if (typeof indexedDB === "undefined") return null;
  const database = await openDatabase();
  try {
    const value = await new Promise<unknown>((resolve, reject) => {
      const request = database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return value ? validateFloorplanDocument(value) : null;
  } finally { database.close(); }
}

export function parseProject(contents: string) {
  return validateFloorplanDocument(JSON.parse(contents));
}

export function downloadProject(document: FloorplanDocumentV2) {
  const blob = new Blob([serializeProject(document)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = `${document.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "floorplan"}.planform.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
